import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  ChevronDown,
  FileText,
  Inbox,
  PenLine,
  ScrollText,
  Search,
  Settings2,
  X,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/copy-button';
import { ImageDialog } from '@/components/ui/image-dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Pagination } from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatCurrency, formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { OrderActionsSheet } from './order-actions-sheet';
import { SlotRange, StatusBadge } from './order-cells';
import { OrderDetail } from './order-detail';
import { OrderFilterBar } from './order-filters';
import { OrderLogsDialog } from './order-logs-dialog';
import { OrderStats } from './order-stats';
import { paymentLabel, typeLabelKey } from './order-enums';
import { EMPTY_FILTERS, useOrderSearch, type OrderFilters } from './orders.api';
import type { Order } from './types';

/**
 * Order search across every store.
 *
 * Unlike the customer lookup this page lists on arrival — the default filter is
 * ACTIVE, so a support agent opens it and sees the live queue.
 *
 * The original tracked `isSearching` by hand and guarded every request with
 * `if (!isSearching)`, so a filter changed while a request was in flight was
 * dropped silently; and two effects plus the submit handler could each start a
 * search, racing each other. One query keyed on the inputs replaces all of it.
 */

export function OrdersPage() {
  const { t } = useTranslation();

  const [draft, setDraft] = useState('');
  const [term, setTerm] = useState('');
  const [filters, setFilters] = useState<OrderFilters>(EMPTY_FILTERS);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [sort, setSort] = useState<'asc' | 'desc'>('desc');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [image, setImage] = useState<{ src: string; title: string } | null>(
    null
  );
  const [logsFor, setLogsFor] = useState<Order | null>(null);
  const [actionsFor, setActionsFor] = useState<Order | null>(null);

  const search = useOrderSearch({ term, filters, page, size, sort });

  const orders = search.data?.orders ?? [];
  const total = search.data?.count ?? 0;
  const revenue = search.data?.sales ?? 0;
  // A result is on screen and the next one is on its way — a filter change
  // (the previous page is kept), a new page, or a reload.
  const busy = search.isFetching && !search.isPending;

  /** Any change to what is being asked for resets to the first page. */
  const applyFilters = (next: OrderFilters) => {
    setFilters(next);
    setPage(0);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('orders.title')}
        description={t('orders.subtitle')}
      />

      <OrderStats
        filters={filters}
        onDrill={(status, time) => applyFilters({ ...filters, status, time })}
      />

      <OrderFilterBar
        filters={filters}
        onChange={applyFilters}
        busy={search.isFetching}
        search={
          <form
            role="search"
            onSubmit={(event) => {
              event.preventDefault();
              // Submitting the same term does not change the query key, so the
              // button would do nothing at all. An explicit refetch makes it
              // double as "reload", which is what it looks like it should do.
              if (draft === term && page === 0) void search.refetch();
              setTerm(draft);
              setPage(0);
            }}
            className="flex gap-2"
          >
            <div className="relative flex-1">
              <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder={t('orders.searchPlaceholder')}
                aria-label={t('orders.searchLabel')}
                className="h-10 pr-9 pl-9"
              />
              {draft && (
                <button
                  type="button"
                  onClick={() => {
                    setDraft('');
                    setTerm('');
                    setPage(0);
                  }}
                  aria-label={t('customers.clearSearch')}
                  className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2 flex size-6 -translate-y-1/2 items-center justify-center rounded"
                >
                  <X className="size-4" />
                </button>
              )}
            </div>
            <Button type="submit" className="h-10">
              <Search className="sm:hidden" />
              <span className="hidden sm:inline">{t('common.search')}</span>
            </Button>
          </form>
        }
      />

      {/* Sort sits with the filters it modifies. */}
      <div className="flex justify-end">
        <div className="w-full sm:w-48">
          <Label htmlFor="order-sort" className="text-muted-foreground text-xs">
            {t('orders.sort')}
          </Label>
          <Select
            value={sort}
            onValueChange={(value) => {
              setSort(value as 'asc' | 'desc');
              setPage(0);
            }}
          >
            <SelectTrigger
              id="order-sort"
              className="w-full"
              disabled={search.isFetching}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="asc">{t('orders.oldest')}</SelectItem>
              <SelectItem value="desc">{t('orders.newest')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Totals for the whole result set, not the visible page — the number a
          support lead is actually after. */}
      {search.isSuccess && (
        <div
          className={cn(
            'flex flex-wrap items-center gap-x-6 gap-y-1 text-sm',
            busy && 'opacity-60 transition-opacity'
          )}
        >
          <span className="text-muted-foreground">
            {t('orders.resultCount', { count: total })}
          </span>
          <span className="text-muted-foreground">
            {t('orders.totalRevenue')}{' '}
            <span className="text-foreground font-semibold tabular-nums">
              {formatCurrency(revenue)}
            </span>
          </span>
        </div>
      )}

      {search.isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>
              {search.error instanceof Error
                ? search.error.message
                : t('orders.errorBody')}
            </p>
            <Button variant="outline" size="sm" onClick={() => search.refetch()}>
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      {search.isPending && <OrdersSkeleton />}

      {search.isSuccess && orders.length === 0 && (
        <div className="relative flex flex-col items-center gap-3 rounded-xl border border-dashed px-6 py-14 text-center">
          <BusyOverlay show={busy} />
          <span className="bg-muted text-muted-foreground flex size-14 items-center justify-center rounded-full">
            <Inbox className="size-7" />
          </span>
          <div className="space-y-1">
            <p className="font-medium">{t('orders.noResultsTitle')}</p>
            <p className="text-muted-foreground max-w-sm text-sm">
              {t('orders.noResultsBody')}
            </p>
          </div>
        </div>
      )}

      {search.isSuccess && orders.length > 0 && (
        // A background refetch keeps the old rows on screen. Each list dims
        // itself under its own spinner — dimming this wrapper as well would
        // fade the spinners along with the rows.
        <div className="space-y-3">
          {/* Desktop */}
          <div className="hidden rounded-xl border lg:block">
            <Table busy={busy}>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-8" />
                  <TableHead>{t('orders.order')}</TableHead>
                  <TableHead>{t('orders.typeColumn')}</TableHead>
                  <TableHead>{t('orders.customer')}</TableHead>
                  <TableHead className="text-right">
                    {t('orders.total')}
                  </TableHead>
                  <TableHead>{t('orders.payment')}</TableHead>
                  <TableHead>{t('orders.statusColumn')}</TableHead>
                  <TableHead>{t('orders.serviceWindow')}</TableHead>
                  <TableHead className="text-center">
                    {t('orders.signature')}
                  </TableHead>
                  <TableHead className="text-center">EBT</TableHead>
                  <TableHead className="text-center">
                    {t('orders.logs')}
                  </TableHead>
                  <TableHead className="text-right">
                    {t('orders.actions')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.map((order) => {
                  const open = expanded === order.id;
                  return (
                    <Fragment key={order.id}>
                      <TableRow
                        onClick={() => setExpanded(open ? null : order.id)}
                        aria-expanded={open}
                        className={cn(
                          'group cursor-pointer',
                          open && 'bg-muted/40 border-b-0'
                        )}
                      >
                        <TableCell>
                          <ChevronDown
                            className={cn(
                              'text-muted-foreground size-4 transition-transform',
                              open && 'rotate-180'
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <p className="font-medium">
                            {order.businessName || '—'}
                          </p>
                          {order.confirmationCode && (
                            <span className="flex items-center gap-1">
                              <span className="text-muted-foreground font-mono text-xs">
                                {order.confirmationCode}
                              </span>
                              <CopyButton value={order.confirmationCode} />
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">
                            {t(typeLabelKey(order.type))}
                          </Badge>
                          {order.deliveryZoneName && (
                            <p className="text-muted-foreground mt-0.5 truncate text-xs">
                              {order.deliveryZoneName}
                            </p>
                          )}
                        </TableCell>
                        <TableCell className="max-w-[12rem] truncate">
                          {order.customerName || '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          <p className="font-medium tabular-nums">
                            {formatCurrency(order.orderTotal)}
                          </p>
                          <p className="text-muted-foreground text-xs tabular-nums">
                            {t('orders.itemCount', {
                              count: order.itemCount ?? 0,
                            })}
                          </p>
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {paymentLabel(order.paymentType)}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={order.status} />
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          <p>{formatDate(order.desiredDateTime)}</p>
                          <p className="text-muted-foreground text-xs">
                            <SlotRange order={order} />
                          </p>
                        </TableCell>
                        <TableCell className="text-center">
                          <DocButton
                            url={order.signatureImageUrl}
                            icon={<PenLine className="size-3.5" />}
                            label={t('orders.signature')}
                            onOpen={setImage}
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <DocButton
                            url={order.ebtReceiptUrl}
                            icon={<FileText className="size-3.5" />}
                            label={t('orders.ebtReceipt')}
                            onOpen={setImage}
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="outline"
                            size="icon-sm"
                            aria-label={t('orders.logs')}
                            title={t('orders.logs')}
                            onClick={(event) => {
                              event.stopPropagation();
                              setLogsFor(order);
                            }}
                          >
                            <ScrollText className="size-3.5" />
                          </Button>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            onClick={(event) => {
                              event.stopPropagation();
                              setActionsFor(order);
                            }}
                          >
                            <Settings2 className="size-3.5" />
                            {t('orders.actions')}
                          </Button>
                        </TableCell>
                      </TableRow>
                      {open && (
                        <TableRow className="hover:bg-transparent">
                          <TableCell colSpan={12} className="bg-muted/40 p-4">
                            <OrderDetail order={order} />
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Phone and tablet. Nine columns cannot be made to work below lg. */}
          <div className="relative lg:hidden">
            <BusyOverlay show={busy} />
            <div
              aria-busy={busy}
              className={cn(
                'space-y-2',
                busy && 'opacity-60 transition-opacity'
              )}
            >
              {orders.map((order) => {
                const open = expanded === order.id;
                return (
                  <div
                    key={order.id}
                    className="bg-card overflow-hidden rounded-xl border"
                  >
                    <button
                      type="button"
                      onClick={() => setExpanded(open ? null : order.id)}
                      aria-expanded={open}
                      className="w-full space-y-2 p-3 text-left"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate font-medium">
                            {order.customerName || '—'}
                          </p>
                          <p className="text-muted-foreground truncate text-xs">
                            {order.businessName}
                            {order.confirmationCode && (
                              <span className="font-mono">
                                {' '}
                                · {order.confirmationCode}
                              </span>
                            )}
                          </p>
                        </div>
                        <ChevronDown
                          className={cn(
                            'text-muted-foreground mt-1 size-4 shrink-0 transition-transform',
                            open && 'rotate-180'
                          )}
                        />
                      </div>

                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                        <StatusBadge status={order.status} />
                        <Badge variant="secondary">
                          {t(typeLabelKey(order.type))}
                        </Badge>
                        <span className="text-sm font-semibold tabular-nums">
                          {formatCurrency(order.orderTotal)}
                        </span>
                        <span className="text-muted-foreground text-xs">
                          {formatDate(order.desiredDateTime)},{' '}
                          <SlotRange order={order} />
                        </span>
                      </div>
                    </button>
                    {open && (
                      <div className="bg-muted/40 space-y-3 border-t p-3">
                        <OrderDetail order={order} />
                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            className="flex-1"
                            onClick={() => setActionsFor(order)}
                          >
                            <Settings2 className="size-3.5" />
                            {t('orders.actions')}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setLogsFor(order)}
                          >
                            <ScrollText className="size-3.5" />
                            {t('orders.logs')}
                          </Button>
                          <DocButton
                            url={order.signatureImageUrl}
                            icon={<PenLine className="size-3.5" />}
                            label={t('orders.signature')}
                            onOpen={setImage}
                          />
                          <DocButton
                            url={order.ebtReceiptUrl}
                            icon={<FileText className="size-3.5" />}
                            label={t('orders.ebtReceipt')}
                            onOpen={setImage}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <Pagination
            page={page}
            size={size}
            total={total}
            onPageChange={setPage}
            onSizeChange={(next) => {
              setSize(next);
              setPage(0);
            }}
          />
        </div>
      )}

      <OrderLogsDialog
        orderId={logsFor?.id ?? null}
        confirmationCode={logsFor?.confirmationCode ?? null}
        open={logsFor !== null}
        onOpenChange={(next) => {
          if (!next) setLogsFor(null);
        }}
      />

      <OrderActionsSheet
        order={actionsFor}
        open={actionsFor !== null}
        onOpenChange={(next) => {
          if (!next) setActionsFor(null);
        }}
      />

      <ImageDialog
        src={image?.src ?? null}
        title={image?.title ?? ''}
        open={image !== null}
        onOpenChange={(next) => {
          if (!next) setImage(null);
        }}
      />
    </div>
  );
}

// ------------------------------------------------------------------ helpers

/**
 * Rendered disabled when the document is absent, as the original did — the
 * column then reads as "this order has no signature" rather than leaving a gap
 * that could equally mean the column failed to render.
 */
function DocButton({
  url,
  icon,
  label,
  onOpen,
}: {
  url: string | null;
  icon: React.ReactNode;
  label: string;
  onOpen: (image: { src: string; title: string }) => void;
}) {
  return (
    <Button
      variant="outline"
      size="icon-sm"
      aria-label={label}
      title={label}
      disabled={!url}
      onClick={(event) => {
        event.stopPropagation();
        if (url) onOpen({ src: url, title: label });
      }}
    >
      {icon}
    </Button>
  );
}

function OrdersSkeleton() {
  return (
    <div className="divide-y rounded-xl border">
      {Array.from({ length: 6 }).map((_, index) => (
        <div key={index} className="flex items-center gap-3 p-3">
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-3 w-32" />
          </div>
          <Skeleton className="h-5 w-20" />
          <Skeleton className="hidden h-5 w-16 sm:block" />
        </div>
      ))}
    </div>
  );
}
